import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { roomTypeService } from "@/lib/api";
import type { ReservationCreate, RoomType } from "@/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { z } from "zod";

const schema = z.object({
  room_type_id: z.string().min(1, "객실 타입을 선택하세요"),
  check_in: z.string().min(1, "체크인 날짜를 입력하세요"),
  check_out: z.string().min(1, "체크아웃 날짜를 입력하세요"),
  guest_name: z.string().min(1, "게스트명을 입력하세요"),
});

type FormValues = z.infer<typeof schema>;

interface ReservationFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roomTypes: RoomType[];
  isPending?: boolean;
  /** Prefill values when opened (e.g. timeline cell drag). */
  defaults?: Partial<Pick<ReservationCreate, "room_type_id" | "check_in" | "check_out">>;
  onSubmit: (values: Omit<ReservationCreate, "property_id">) => void;
}

export function ReservationFormDialog({
  open,
  onOpenChange,
  roomTypes,
  isPending,
  defaults,
  onSubmit,
}: ReservationFormDialogProps) {
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const watched = useWatch({ control });
  const { room_type_id, check_in, check_out } = watched as Partial<FormValues>;
  const datesValid = Boolean(room_type_id && check_in && check_out && check_in < check_out);
  const { data: availability } = useQuery({
    queryKey: ["reservation-availability", room_type_id, check_in, check_out],
    queryFn: () =>
      roomTypeService.availability(room_type_id as string, check_in as string, check_out as string),
    enabled: datesValid,
  });
  const minAvail = availability?.length ? Math.min(...availability.map((d) => d.available)) : null;

  useEffect(() => {
    if (open) {
      reset({
        room_type_id: defaults?.room_type_id ?? "",
        check_in: defaults?.check_in ?? "",
        check_out: defaults?.check_out ?? "",
        guest_name: "",
      });
    }
  }, [open, reset, defaults]);

  const submit = handleSubmit((values) => {
    onSubmit({
      room_type_id: values.room_type_id,
      check_in: values.check_in,
      check_out: values.check_out,
      guest_name: values.guest_name.trim(),
    });
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>새 예약</DialogTitle>
          <DialogDescription>
            직접 예약을 등록합니다. 가용 객실이 있을 때만 생성됩니다.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="rsv-guest-name">게스트명 *</Label>
            <Input id="rsv-guest-name" {...register("guest_name")} placeholder="홍길동" />
            {errors.guest_name ? (
              <p className="text-xs text-destructive">{errors.guest_name.message}</p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="rsv-room-type">객실 타입 *</Label>
            <Controller
              control={control}
              name="room_type_id"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="rsv-room-type">
                    <SelectValue placeholder="타입 선택…" />
                  </SelectTrigger>
                  <SelectContent>
                    {roomTypes
                      .filter((rt) => rt.is_active)
                      .map((rt) => (
                        <SelectItem key={rt.id} value={rt.id}>
                          {rt.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.room_type_id ? (
              <p className="text-xs text-destructive">{errors.room_type_id.message}</p>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="rsv-check-in">체크인 *</Label>
              <Input id="rsv-check-in" type="date" {...register("check_in")} />
              {errors.check_in ? (
                <p className="text-xs text-destructive">{errors.check_in.message}</p>
              ) : null}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rsv-check-out">체크아웃 *</Label>
              <Input id="rsv-check-out" type="date" {...register("check_out")} />
              {errors.check_out ? (
                <p className="text-xs text-destructive">{errors.check_out.message}</p>
              ) : null}
            </div>
          </div>

          {datesValid && minAvail !== null ? (
            <p className={`text-sm ${minAvail > 0 ? "text-muted-foreground" : "text-destructive"}`}>
              {minAvail > 0 ? `가용 ${minAvail}실` : "예약 불가 — 가용 객실 없음"}
            </p>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              취소
            </Button>
            <Button type="submit" disabled={isPending || (datesValid && minAvail === 0)}>
              {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              등록
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
